import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import style from "./DonationImpactItemGlobalHealth.module.scss";
import { thousandize, thousandizeString } from "../../../../../util/formatting";
import useSWR from "swr";
import { ImpactEvaluation } from "../../../../../models";
import AnimateHeight from "react-animate-height";
import { LinkType, Links } from "../../../../main/blocks/Links/Links";
import { PortableText } from "@portabletext/react";
import { NavLink } from "../../../../shared/components/Navbar/Navbar";
const fetcher = (url: string) => fetch(url).then(res => res.json());
const DEFAULT_GIVEWELL_ALL_GRANTS_TEXT = "All Grants Fund lar GiveWells eksperter investere i de mest lovende mulighetene på tvers av deres arbeid. Fordi fondet kombinerer både velprøvde og mer eksperimentelle tiltak, oppnår det høyere forventet effekt enn fokus på færre, kjente interventioner alene. Fondet støtter for eksempel utrulling av nye malariavaksiner i Afrika og prosesser som kan få nye legemidler raskere ut til dem som trenger det. Når du donerer til All Grants Fund, stoler du på GiveWells analysekompetanse, og får maksimal effekt per krone.";
const DEFAULT_GIVEWELL_ALL_GRANTS_LINKS: LinkType[] = [{
  _type: "link",
  _key: "givewell-all-grants-fund",
  title: "Les mer hos GiveWell",
  url: "https://www.givewell.org/all-grants-fund",
  newtab: true
}];
export type ImpactItemConfiguration = {
  output_subheading_format_string: string;
  missing_evaluation_header: string;
  missing_impact_evaluation_text: any[];
  about_org_link_title_format_string: string;
  about_org_link_url_format_string: string;
  currency: string;
  locale: string;
  givewell_all_grants_fund_header?: string;
  givewell_all_grants_fund_text?: any[];
  givewell_all_grants_fund_links?: (LinkType | NavLink)[];
};
const renderGiveWellAllGrantsFundContent = (configuration: ImpactItemConfiguration) => {
  const customText = configuration.givewell_all_grants_fund_text;
  const customLinks = configuration.givewell_all_grants_fund_links;
  const linksToRender = customLinks && customLinks.length > 0 ? customLinks : DEFAULT_GIVEWELL_ALL_GRANTS_LINKS;
  return <>
      {customText && customText.length > 0 ? <PortableText value={customText} /> : <p>{DEFAULT_GIVEWELL_ALL_GRANTS_TEXT}</p>}
      {linksToRender.length > 0 && <Links links={linksToRender} />}
    </>;
};
export type PreComputedImpact = {
  output: number;
  shortDescription: string;
  longDescription: string;
  charityName: string;
  orgUrl: string;
};
export const DonationImpactGlobalHealthItem: React.FC<{
  orgAbriv: string;
  orgName: string;
  sumToOrg: number;
  // Portion of `sumToOrg` routed via a GiveWell grant, and the outputs it bought
  // using the grant's own cost-per-output
  smartDistributionSum?: number;
  smartDistributionOutput?: number;
  donationTimestamp: Date;
  precision: number;
  signalRequiredPrecision: (precision: number) => void;
  configuration: ImpactItemConfiguration;
  preComputedImpact?: PreComputedImpact;
}> = t0 => {
  const $ = _c(105);
  const {
    orgAbriv,
    orgName,
    sumToOrg,
    smartDistributionSum,
    smartDistributionOutput,
    donationTimestamp,
    precision,
    signalRequiredPrecision,
    configuration,
    preComputedImpact
  } = t0;
  let t1;
  if ($[0] !== configuration || $[1] !== donationTimestamp || $[2] !== orgAbriv || $[3] !== preComputedImpact) {
    t1 = preComputedImpact ? null : `https://impact.gieffektivt.no/api/evaluations?charity_abbreviation=${orgAbriv}&currency=${configuration.currency}&language=${configuration.locale}&donation_year=${donationTimestamp.getFullYear()}&donation_month=${donationTimestamp.getMonth() + 1}`;
    $[0] = configuration;
    $[1] = donationTimestamp;
    $[2] = orgAbriv;
    $[3] = preComputedImpact;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data,
    error,
    isValidating
  } = useSWR(t1, fetcher, t2);
  const [showDetails, setShowDetails] = useState(false);
  const [requiredPrecision, setRequiredPrecision] = useState(0);
  let t3;
  if ($[6] !== precision || $[7] !== requiredPrecision || $[8] !== signalRequiredPrecision) {
    t3 = () => {
      if (precision < requiredPrecision) {
        signalRequiredPrecision(requiredPrecision);
      }
    };
    $[6] = precision;
    $[7] = requiredPrecision;
    $[8] = signalRequiredPrecision;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== precision || $[11] !== requiredPrecision) {
    t4 = [precision, requiredPrecision];
    $[10] = precision;
    $[11] = requiredPrecision;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  if (!preComputedImpact) {
    if (!data || isValidating) {
      let t5;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <tr key="loading"><td>Loading...</td></tr>;
        $[13] = t5;
      } else {
        t5 = $[13];
      }
      return t5;
    }
    if (error) {
      let t5;
      if ($[14] !== error) {
        t5 = <tr key="error"><td>{error}</td></tr>;
        $[14] = error;
        $[15] = t5;
      } else {
        t5 = $[15];
      }
      return t5;
    }
  }
  const relevantEvaluation = data?.evaluations[0];
  const grantOutput = smartDistributionOutput ?? 0;
  const directSum = sumToOrg - (smartDistributionSum ?? 0);
  let t5;
  if ($[16] !== configuration.about_org_link_url_format_string || $[17] !== directSum || $[18] !== grantOutput || $[19] !== preComputedImpact || $[20] !== relevantEvaluation) {
    t5 = preComputedImpact ? preComputedImpact : relevantEvaluation ? {
      output: grantOutput + directSum / relevantEvaluation.converted_cost_per_output,
      shortDescription: relevantEvaluation.intervention.short_description,
      longDescription: relevantEvaluation.intervention.long_description,
      charityName: relevantEvaluation.charity.charity_name,
      orgUrl: configuration.about_org_link_url_format_string.replace("{{org}}", relevantEvaluation.charity.charity_name).replaceAll(" ", "_")
    } : null;
    $[16] = configuration.about_org_link_url_format_string;
    $[17] = directSum;
    $[18] = grantOutput;
    $[19] = preComputedImpact;
    $[20] = relevantEvaluation;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const resolvedImpact = t5;
  const isGiveWellAllGrantsFund = orgAbriv === "AGF";
  const missingEvaluationHeader = isGiveWellAllGrantsFund ? configuration.givewell_all_grants_fund_header ?? configuration.missing_evaluation_header : configuration.missing_evaluation_header;
  if (!resolvedImpact) {
    let t6;
    if ($[22] !== sumToOrg) {
      t6 = thousandize(Math.round(sumToOrg));
      $[22] = sumToOrg;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    let t7;
    if ($[24] !== t6) {
      t7 = <td><span className={style.impactOutput} data-cy="donation-impact-list-item-output">{t6}</span></td>;
      $[24] = t6;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    let t8;
    if ($[26] !== configuration.output_subheading_format_string || $[27] !== orgName || $[28] !== sumToOrg) {
      t8 = configuration.output_subheading_format_string.replace("{{sum}}", thousandize(Math.round(sumToOrg))).replace("{{org}}", orgName);
      $[26] = configuration.output_subheading_format_string;
      $[27] = orgName;
      $[28] = sumToOrg;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    let t9;
    if ($[30] !== t8) {
      t9 = <span className={style.impactDetailsDescription}>{" "}{t8}</span>;
      $[30] = t8;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    const t10 = showDetails ? style.expanded : "";
    let t11;
    if ($[32] !== t10) {
      t11 = [style.impactDetailsExpandText, t10];
      $[32] = t10;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    const t12 = t11.join(" ");
    let t13;
    if ($[34] !== showDetails) {
      t13 = () => setShowDetails(!showDetails);
      $[34] = showDetails;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t14;
    if ($[36] !== missingEvaluationHeader || $[37] !== t12 || $[38] !== t13) {
      t14 = <span className={t12} onClick={t13}>{missingEvaluationHeader}</span>;
      $[36] = missingEvaluationHeader;
      $[37] = t12;
      $[38] = t13;
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    let t15;
    if ($[40] !== t14 || $[41] !== t9) {
      t15 = <td><div className={style.impactContext}>{t9}{t14}</div></td>;
      $[40] = t14;
      $[41] = t9;
      $[42] = t15;
    } else {
      t15 = $[42];
    }
    let t16;
    if ($[43] !== t15 || $[44] !== t7) {
      t16 = <tr className={style.overview} data-cy="donation-impact-list-item-overview">{t7}{t15}</tr>;
      $[43] = t15;
      $[44] = t7;
      $[45] = t16;
    } else {
      t16 = $[45];
    }
    const t17 = showDetails ? "auto" : 0;
    let t18;
    if ($[46] !== configuration || $[47] !== isGiveWellAllGrantsFund) {
      t18 = isGiveWellAllGrantsFund ? renderGiveWellAllGrantsFundContent(configuration) : configuration.missing_impact_evaluation_text && <PortableText value={configuration.missing_impact_evaluation_text} />;
      $[46] = configuration;
      $[47] = isGiveWellAllGrantsFund;
      $[48] = t18;
    } else {
      t18 = $[48];
    }
    let t19;
    if ($[49] !== t18) {
      t19 = <div>{t18}</div>;
      $[49] = t18;
      $[50] = t19;
    } else {
      t19 = $[50];
    }
    let t20;
    if ($[51] !== t17 || $[52] !== t19) {
      t20 = <tr className={style.details}><td colSpan={Number.MAX_SAFE_INTEGER}><AnimateHeight duration={300} animateOpacity={true} height={t17}>{t19}</AnimateHeight></td></tr>;
      $[51] = t17;
      $[52] = t19;
      $[53] = t20;
    } else {
      t20 = $[53];
    }
    let t21;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <tr className={style.spacerRow}><td colSpan={Number.MAX_SAFE_INTEGER} /></tr>;
      $[54] = t21;
    } else {
      t21 = $[54];
    }
    let t22;
    if ($[55] !== t16 || $[56] !== t20) {
      t22 = <>{t16}{t20}{t21}</>;
      $[55] = t16;
      $[56] = t20;
      $[57] = t22;
    } else {
      t22 = $[57];
    }
    return t22;
  }
  const output = resolvedImpact.output;
  let tmpRequiredPrecision = 0;
  if ($[58] !== output || $[59] !== tmpRequiredPrecision) {
    while (parseFloat(output.toFixed(tmpRequiredPrecision)) === 0 && tmpRequiredPrecision < 3) {
      tmpRequiredPrecision = tmpRequiredPrecision + 1;
    }
    $[58] = output;
    $[59] = tmpRequiredPrecision;
    $[60] = tmpRequiredPrecision;
  } else {
    tmpRequiredPrecision = $[60];
  }
  if (requiredPrecision < tmpRequiredPrecision) {
    setRequiredPrecision(tmpRequiredPrecision);
  }
  const t6 = Math.round(output * Math.pow(10, requiredPrecision));
  const t7 = Math.pow(10, requiredPrecision);
  let t8;
  if ($[61] !== precision || $[62] !== t6 || $[63] !== t7) {
    t8 = thousandizeString((t6 / t7).toFixed(precision).replace(/\./, ","));
    $[61] = precision;
    $[62] = t6;
    $[63] = t7;
    $[64] = t8;
  } else {
    t8 = $[64];
  }
  const formattedOutput = t8;
  let t9;
  if ($[65] !== formattedOutput) {
    t9 = <td><span className={style.impactOutput} data-cy="donation-impact-list-item-output">{formattedOutput}</span></td>;
    $[65] = formattedOutput;
    $[66] = t9;
  } else {
    t9 = $[66];
  }
  let t10;
  if ($[67] !== resolvedImpact.shortDescription) {
    t10 = <span className={style.impactDetailsDescription}>{resolvedImpact.shortDescription}</span>;
    $[67] = resolvedImpact.shortDescription;
    $[68] = t10;
  } else {
    t10 = $[68];
  }
  const t11 = showDetails ? style.expanded : "";
  let t12;
  if ($[69] !== t11) {
    t12 = [style.impactDetailsExpandText, t11];
    $[69] = t11;
    $[70] = t12;
  } else {
    t12 = $[70];
  }
  const t13 = t12.join(" ");
  let t14;
  if ($[71] !== showDetails) {
    t14 = () => setShowDetails(!showDetails);
    $[71] = showDetails;
    $[72] = t14;
  } else {
    t14 = $[72];
  }
  let t15;
  if ($[73] !== configuration.output_subheading_format_string || $[74] !== resolvedImpact.charityName || $[75] !== sumToOrg) {
    t15 = configuration.output_subheading_format_string.replace("{{sum}}", thousandize(Math.round(sumToOrg))).replace("{{org}}", resolvedImpact.charityName);
    $[73] = configuration.output_subheading_format_string;
    $[74] = resolvedImpact.charityName;
    $[75] = sumToOrg;
    $[76] = t15;
  } else {
    t15 = $[76];
  }
  let t16;
  if ($[77] !== t13 || $[78] !== t14 || $[79] !== t15) {
    t16 = <span className={t13} onClick={t14}>{t15}</span>;
    $[77] = t13;
    $[78] = t14;
    $[79] = t15;
    $[80] = t16;
  } else {
    t16 = $[80];
  }
  let t17;
  if ($[81] !== t10 || $[82] !== t16) {
    t17 = <td><div className={style.impactContext}>{t10}{t16}</div></td>;
    $[81] = t10;
    $[82] = t16;
    $[83] = t17;
  } else {
    t17 = $[83];
  }
  let t18;
  if ($[84] !== t17 || $[85] !== t9) {
    t18 = <tr className={style.overview} data-cy="donation-impact-list-item-overview">{t9}{t17}</tr>;
    $[84] = t17;
    $[85] = t9;
    $[86] = t18;
  } else {
    t18 = $[86];
  }
  const t19 = showDetails ? "auto" : 0;
  let t20;
  if ($[87] !== resolvedImpact.longDescription) {
    t20 = <p>{resolvedImpact.longDescription}</p>;
    $[87] = resolvedImpact.longDescription;
    $[88] = t20;
  } else {
    t20 = $[88];
  }
  let t21;
  if ($[89] !== configuration.about_org_link_title_format_string || $[90] !== resolvedImpact.charityName) {
    t21 = configuration.about_org_link_title_format_string.replace("{{org}}", resolvedImpact.charityName);
    $[89] = configuration.about_org_link_title_format_string;
    $[90] = resolvedImpact.charityName;
    $[91] = t21;
  } else {
    t21 = $[91];
  }
  let t22;
  if ($[92] !== resolvedImpact.orgUrl || $[93] !== t21) {
    t22 = <div><Links links={[{
        _type: "link",
        _key: "charity_description",
        title: t21,
        url: resolvedImpact.orgUrl,
        newtab: true
      }]} /></div>;
    $[92] = resolvedImpact.orgUrl;
    $[93] = t21;
    $[94] = t22;
  } else {
    t22 = $[94];
  }
  let t23;
  if ($[95] !== t20 || $[96] !== t22) {
    t23 = <div>{t20}{t22}</div>;
    $[95] = t20;
    $[96] = t22;
    $[97] = t23;
  } else {
    t23 = $[97];
  }
  let t24;
  if ($[98] !== t19 || $[99] !== t23) {
    t24 = <tr className={style.details}><td colSpan={Number.MAX_SAFE_INTEGER}><AnimateHeight duration={300} animateOpacity={true} height={t19}>{t23}</AnimateHeight></td></tr>;
    $[98] = t19;
    $[99] = t23;
    $[100] = t24;
  } else {
    t24 = $[100];
  }
  let t25;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <tr className={style.spacerRow}><td colSpan={Number.MAX_SAFE_INTEGER} /></tr>;
    $[101] = t25;
  } else {
    t25 = $[101];
  }
  let t26;
  if ($[102] !== t18 || $[103] !== t24) {
    t26 = <>{t18}{t24}{t25}</>;
    $[102] = t18;
    $[103] = t24;
    $[104] = t26;
  } else {
    t26 = $[104];
  }
  return t26;
};